import Pusher from "pusher-js"
import { http } from "../api/axios"
import endpoints from "../api/endpoints"

if (process.env.REACT_APP_DEBUG_PUSHER === "true") {
  Pusher.logToConsole = true
}

/**
 * Centralized Pusher client.
 *
 * Presence flow:
 *   Connect → socket_id → POST /api/v1/socket/auth → subscribe presence-portal
 *
 * NEVER put PUSHER_APP_SECRET (or REACT_APP_PUSHER_APP_SECRET) in the client.
 */

let pusherInstance = null
let authTokenGetter = null
let instanceSkipAuth = null
let reconnectGuardsBound = false
const channelRefCounts = new Map()
const pendingAuth = new Map()

const getKey = () => process.env.REACT_APP_PUSHER_APP_KEY || ""
const getCluster = () => process.env.REACT_APP_PUSHER_APP_CLUSTER || "us2"

/**
 * Skip auth for private (non-presence) channels until those endpoints are ready.
 * Presence always uses backend socket auth. Set REACT_APP_PUSHER_SKIP_AUTH=false
 * to also authorize private message channels.
 */
export const isPusherAuthSkipped = () =>
  process.env.REACT_APP_PUSHER_SKIP_AUTH !== "false"

/**
 * Auth endpoint for private/presence channels.
 * Prefer REACT_APP_PUSHER_AUTH_ENDPOINT; fall back to API socket auth.
 */
export const getAuthEndpoint = () => {
  if (process.env.REACT_APP_PUSHER_AUTH_ENDPOINT) {
    return process.env.REACT_APP_PUSHER_AUTH_ENDPOINT
  }
  return endpoints.pusherAuth || "/api/v1/socket/auth"
}

const isPrivateChannel = name => String(name || "").startsWith("private-")

const reconnectIfNeeded = () => {
  if (!pusherInstance) return
  const state = pusherInstance.connection?.state
  if (
    state === "disconnected" ||
    state === "failed" ||
    state === "unavailable"
  ) {
    try {
      pusherInstance.connect()
    } catch {
      // ignore reconnect races
    }
  }
}

const bindReconnectGuards = () => {
  if (reconnectGuardsBound || typeof window === "undefined") return
  reconnectGuardsBound = true

  window.addEventListener("online", reconnectIfNeeded)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") reconnectIfNeeded()
  })
}

const unwrapPusherAuth = data => {
  if (!data || typeof data !== "object") return null
  if (data.success === false) return null

  const payload = data.result || data.data || data
  if (!payload?.auth) return null

  const authPayload = { auth: payload.auth }
  if (payload.channel_data != null) {
    authPayload.channel_data =
      typeof payload.channel_data === "string"
        ? payload.channel_data
        : JSON.stringify(payload.channel_data)
  }
  return authPayload
}

const authorizeSocket = (socketId, channelName) => {
  const key = `${socketId}:${channelName}`
  const inFlight = pendingAuth.get(key)
  if (inFlight) return inFlight

  const request = (async () => {
    const token =
      typeof authTokenGetter === "function" ? authTokenGetter() : null

    if (!socketId) {
      throw new Error("Missing Pusher socket_id for channel auth")
    }
    if (!token) {
      throw new Error("Missing auth token for Pusher channel auth")
    }

    const body = {
      socket_id: socketId,
      channel_name: channelName,
    }

    const requestAuth = contentType =>
      http.post(getAuthEndpoint(), body, {
        skipErrorToast: true,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": contentType,
        },
      })

    let response
    try {
      response = await requestAuth("application/json")
    } catch (error) {
      const status = error?.response?.status
      if (status === 415 || status === 422) {
        response = await http.post(
          getAuthEndpoint(),
          new URLSearchParams(body).toString(),
          {
            skipErrorToast: true,
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/x-www-form-urlencoded",
            },
          },
        )
      } else {
        throw error
      }
    }

    const authPayload = unwrapPusherAuth(response?.data)
    if (!authPayload) {
      throw new Error(
        response?.data?.message || "Pusher socket authentication failed",
      )
    }
    return authPayload
  })().finally(() => {
    pendingAuth.delete(key)
  })

  pendingAuth.set(key, request)
  return request
}

const buildPusherOptions = () => ({
  cluster: getCluster(),
  forceTLS: true,
  authEndpoint: getAuthEndpoint(),
  authorizer: channel => ({
    authorize: (socketId, callback) => {
      authorizeSocket(socketId, channel.name)
        .then(authPayload => {
          callback(null, authPayload)
        })
        .catch(error => {
          callback(error, null)
        })
    },
  }),
})

/**
 * @param {{ getAuthToken?: () => string | null | undefined }} [options]
 */
export const getPusher = (options = {}) => {
  if (typeof options.getAuthToken === "function") {
    authTokenGetter = options.getAuthToken
  }

  const skipAuth = isPusherAuthSkipped()
  if (pusherInstance && instanceSkipAuth !== skipAuth) {
    try {
      pusherInstance.disconnect()
    } catch {
      // ignore
    }
    pusherInstance = null
    channelRefCounts.clear()
    pendingAuth.clear()
  }

  if (pusherInstance) {
    return pusherInstance
  }

  const key = getKey()
  if (!key) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(
        "[pusher] REACT_APP_PUSHER_APP_KEY is missing. Presence will not connect.",
      )
    }
    return null
  }

  pusherInstance = new Pusher(key, buildPusherOptions())
  instanceSkipAuth = skipAuth
  bindReconnectGuards()
  if (pusherInstance.connection?.state === "disconnected") {
    pusherInstance.connect()
  }

  return pusherInstance
}

export const disconnectPusher = () => {
  if (!pusherInstance) return
  try {
    pusherInstance.disconnect()
  } catch {
    // ignore disconnect errors during teardown
  }
  pusherInstance = null
  authTokenGetter = null
  instanceSkipAuth = null
  channelRefCounts.clear()
  pendingAuth.clear()
}

/**
 * Subscribe with ref-counting so React Strict Mode / overlapping
 * conversation views do not create duplicate Pusher channels.
 * Does not disconnect the shared client — PresenceProvider owns that.
 */
export const ensurePusher = (options = {}) => {
  const pusher = getPusher(options)
  if (!pusher) return null
  const state = pusher.connection?.state
  if (
    state === "disconnected" ||
    state === "failed" ||
    state === "unavailable"
  ) {
    pusher.connect()
  }
  return pusher
}

export const subscribeChannel = name => {
  const pusher = ensurePusher()
  if (!pusher || !name) return null

  // Presence always authenticates via /socket/auth. Private message
  // channels still honor skip-auth until that backend path is enabled.
  if (isPusherAuthSkipped() && isPrivateChannel(name)) {
    return null
  }

  const nextCount = (channelRefCounts.get(name) || 0) + 1
  channelRefCounts.set(name, nextCount)
  return pusher.subscribe(name)
}

export const unsubscribeChannel = name => {
  if (!name) return

  const count = channelRefCounts.get(name) || 0
  if (count <= 1) {
    channelRefCounts.delete(name)
    try {
      pusherInstance?.unsubscribe(name)
    } catch {
      // ignore teardown races
    }
    return
  }

  channelRefCounts.set(name, count - 1)
}

export const isPusherConnected = () =>
  Boolean(pusherInstance && pusherInstance.connection?.state === "connected")
